import type { AdminKycVerification } from '@entities/admin-user/model/kyc-verification'
import type { UserReceivingPlan } from './user-receiving-plan'

export type ReserveSync = Pick<AdminKycVerification, 'linked' | 'reserve'>
export const RESERVE_CONFIRMATION_TIMEOUT_MS = 120_000
export type ReserveSyncState = 'unlinked' | 'applied' | 'pending' | 'failed' | 'unconfirmed'

export function reserveSyncState(
  plan: UserReceivingPlan,
  data: ReserveSync,
  confirmationTimedOut = false,
): ReserveSyncState {
  if (!data.linked) return 'unlinked'
  const terms = plan.fees.find((fee) => fee.currency === 'BRL')!
  const matches = (
    value:
      | NonNullable<ReserveSync['reserve']>['desired']
      | NonNullable<ReserveSync['reserve']>['applied'],
  ) =>
    value != null &&
    value.currency.toUpperCase() === 'BRL' &&
    Number(value.percent) === Number(terms.nationalReservePercentage) &&
    value.daysAfterCharge === terms.nationalReserveDays
  if (matches(data.reserve?.applied ?? null)) return 'applied'
  if (terms.nationalReserveDays < 3 || terms.nationalReserveDays > 180) return 'failed'
  if (matches(data.reserve?.desired ?? null) && ['failed', 'dead'].includes(data.reserve!.status))
    return 'failed'
  return confirmationTimedOut ? 'unconfirmed' : 'pending'
}

export function reserveFailureMessage(plan: UserReceivingPlan, data: ReserveSync): string {
  const days = plan.fees.find((fee) => fee.currency === 'BRL')?.nationalReserveDays
  if (days != null && (days < 3 || days > 180))
    return 'Revise o prazo da reserva. O prazo permitido é de 3 a 180 dias.'
  switch (data.reserve?.lastErrorCode) {
    case 'invalid_terms':
      return 'As condições da reserva são inválidas. Revise o percentual e o prazo configurados.'
    case 'provider_validation':
      return 'A Stripe recusou as condições da reserva. Consulte o suporte antes de alterar o plano.'
    case 'provider_unavailable':
      return 'Não foi possível confirmar a reserva com a Stripe. Consulte o suporte se a falha persistir.'
    case 'configuration_unavailable':
      return 'A configuração da integração está indisponível. Acione o suporte para verificar a aplicação.'
    case 'immutable_terms_changed':
    case 'reserve_plan_not_active':
      return 'O plano de reserva na Stripe precisa de revisão. Acione o suporte para verificar a aplicação.'
    case 'reconcile_pending':
      return 'A aplicação ainda precisa ser conciliada com a Stripe. Acione o suporte se a pendência persistir.'
    default:
      return 'Não foi possível aplicar a reserva. Acione o suporte para verificar a causa.'
  }
}
